import {AnyAction, createAsyncThunk, createSlice, PayloadAction, ThunkDispatch} from '@reduxjs/toolkit';
import { IPolyathlonPointList } from '@App/types';
import { RootState } from '@App/store';

import pointListApi from '@App/services/pointList/pointListApi';
import { setActiveTab } from '@Features/pointsScreen/pointsScreenSlice';
import { pollingSucceeded, pollingFailed } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';
import { eventsApi } from '@App/services/events/eventsApi';

import { pollingErrorHandlerSlice } from '@Features/errorHandler/polling/pollingErrorHandlerSlice';

export interface IPolyathlonPointListState {
  changeTimestamp: number; // Tohle trigeruje mount/unmout pooling kontejneru, tak aby se forsnul reload při znovu kliku (jinak se vysypal state)
  dataLoaded: boolean;
  isFetching: boolean;
  isLive: boolean;
  error: any;
  data: IPolyathlonPointList | null;
}

const initialState: IPolyathlonPointListState = {
  changeTimestamp: 0,
  dataLoaded: false,
  isFetching: false,
  isLive: true,
  error: null,
  data: null,
};

export const getPolyathlonPointListDataAsync = createAsyncThunk(
  'polyathlonPointList/fetchData',
  async (timeTableId: number, { dispatch, rejectWithValue, signal, getState }) => {
    try {
      const isLive = selectIsLive(getState() as RootState);
      const response = await pointListApi.fetchPolyathlonPointListData(timeTableId, isLive, signal);

      const thunkDispatch = dispatch as ThunkDispatch<any, any, AnyAction>; // Nutno to přetypovat, TypeScript si s tím nějak
      thunkDispatch(eventsApi.util.updateQueryData('getEventsStatesPoints', '', draft => {
        draft[timeTableId] = response.eventState;
      }));

      dispatch(pollingSucceeded());

      return response.data;
    } catch (err) {
      dispatch(pollingFailed({ sectionName: 'PolyathlonPointList', error: err }));
      return rejectWithValue(err);
    }
  }
);

export const polyathlonPointListSlice = createSlice({
  name: 'polyathlonPointListSlice',
  initialState,
  reducers: {
      setIsLive: (state, action: PayloadAction<boolean>) => {
        state.isLive = action.payload;
      }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPolyathlonPointListDataAsync.pending, state => {
        state.isFetching = true;
      })
      .addCase(getPolyathlonPointListDataAsync.fulfilled, (state, action) => {
        state.isFetching = false;
        state.dataLoaded = true;
        state.data = action.payload;
      })
      .addCase(getPolyathlonPointListDataAsync.rejected, (state, action) => {
        if (action.error.name !== 'AbortError') {
          state.isFetching = false;
          state.error = action.payload;
        }
      })
      .addCase(setActiveTab, (state, action) => { // Clear state
        if (action.payload.activeTab && action.payload.changed) {
          Object.assign(state, initialState);
        }
      })
      .addCase(eventsApi.internalActions.unsubscribeQueryResult, state => { // Clear state when moving to different screen
        Object.assign(state, initialState);
      })
      .addCase(pollingErrorHandlerSlice.actions.clearPollingErrors, (state) => {
        state.error = null;
      });
  }
});

export const selectIsFetching = (state: RootState) => state.polyathlonPoints.isFetching;
export const selectError = (state: RootState) => state.polyathlonPoints.error;
export const selectDataLoaded = (state: RootState) => state.polyathlonPoints.dataLoaded;
export const selectData = (state: RootState) => state.polyathlonPoints.data;
export const selectIsLive = (state: RootState) => state.polyathlonPoints.isLive;
export const selectChangeTimestamp = (state: RootState) => state.polyathlonPoints.changeTimestamp;

export const {
  setIsLive
} = polyathlonPointListSlice.actions

export default polyathlonPointListSlice.reducer;